import { ORGANIZATION } from '~/constants/organization'

/**
 * Sets title + description + OG/Twitter for a static page from two i18n keys:
 *   <prefix>.seoTitle        — short topic; layout titleTemplate appends ` | vodi4ka.com`
 *   <prefix>.seoDescription  — 120-160 chars, unique per page and per locale
 *
 * Exists so BG and EN metadata cannot drift: adding a page means adding two
 * keys to BOTH i18n/locales/bg.json and i18n/locales/en.json, and the missing
 * one shows up immediately as a raw key in the rendered title.
 *
 * Canonical and hreflang are NOT set here — app/layouts/default.vue already
 * applies them site-wide (see docs/seo-audit-2026-08.md).
 */
export function useDefaultPageSeo(keyPrefix: string) {
  const { t, locale } = useI18n()
  const { canonicalUrl } = useCanonicalUrl()
  const config = useRuntimeConfig()
  const siteUrl = (config.public.siteUrl as string).replace(/\/$/, '')
  const ogImage = `${siteUrl}${ORGANIZATION.logoPath}`

  const title = computed(() => t(`${keyPrefix}.seoTitle`))
  const description = computed(() => t(`${keyPrefix}.seoDescription`))
  const documentTitle = computed(() => formatDocumentTitle(title.value))

  useSeoMeta({
    title: () => title.value,
    description: () => description.value,
    ogTitle: () => documentTitle.value,
    ogDescription: () => description.value,
    ogUrl: () => canonicalUrl.value,
    ogImage,
    ogLocale: () => (locale.value === 'en' ? 'en_US' : 'bg_BG'),
    twitterTitle: () => documentTitle.value,
    twitterDescription: () => description.value,
    twitterImage: ogImage,
    twitterCard: 'summary',
  })
}
